/**
 * Permission Keys — single source of truth for the frontend.
 *
 * These keys MUST match the `key` column in the backend `permissions` table.
 * Backend source: backend-template/src/common/constants/permissions.ts
 */

export const PERMISSIONS = {
  DASHBOARD: {
    OVERVIEW_VIEW_ANY: "dashboard.overview.view_any",
    OVERVIEW_VIEW_OWN: "dashboard.overview.view_own",
  },

  PRODUCTS: {
    CREATE: "products.create",
    VIEW_ANY: "products.view_any",
    VIEW_OWN: "products.view_own",
    EDIT_ANY: "products.edit_any",
    EDIT_OWN: "products.edit_own",
    DELETE_ANY: "products.delete_any",
    DELETE_OWN: "products.delete_own",
  },

  CATEGORIES: {
    CREATE: "categories.create",
    VIEW_ANY: "categories.view_any",
    VIEW_OWN: "categories.view_own",
    EDIT_ANY: "categories.edit_any",
    EDIT_OWN: "categories.edit_own",
    DELETE_ANY: "categories.delete_any",
    DELETE_OWN: "categories.delete_own",
  },

  MEDIA: {
    CREATE: "media.create",
    VIEW_ANY: "media.view_any",
    VIEW_OWN: "media.view_own",
    EDIT_ANY: "media.edit_any",
    EDIT_OWN: "media.edit_own",
    DELETE_ANY: "media.delete_any",
    DELETE_OWN: "media.delete_own",
  },

  WEBSITE: {
    HOMEPAGE: {
      VIEW_DRAFT: "website.homepage.view_draft",
      EDIT: "website.homepage.edit",
      PUBLISH: "website.homepage.publish",
    },
    ABOUT_US: {
      VIEW_DRAFT: "website.about_us.view_draft",
      EDIT: "website.about_us.edit",
      PUBLISH: "website.about_us.publish",
    },
    CONTACT_US: {
      VIEW_DRAFT: "website.contact_us.view_draft",
      EDIT: "website.contact_us.edit",
      PUBLISH: "website.contact_us.publish",
    },
    CATEGORY_LANDING: {
      VIEW_DRAFT: "website.category_landing.view_draft",
      EDIT: "website.category_landing.edit",
      PUBLISH: "website.category_landing.publish",
    },
    PRODUCT_LISTING: {
      VIEW_DRAFT: "website.product_listing.view_draft",
      EDIT: "website.product_listing.edit",
      PUBLISH: "website.product_listing.publish",
    },
    BANNERS: {
      CREATE: "website.banners.create",
      VIEW_ANY: "website.banners.view_any",
      VIEW_OWN: "website.banners.view_own",
      EDIT_ANY: "website.banners.edit_any",
      EDIT_OWN: "website.banners.edit_own",
      DELETE_ANY: "website.banners.delete_any",
      DELETE_OWN: "website.banners.delete_own",
    },
  },

  CONTACT_INQUIRIES: {
    VIEW_ANY: "contact_inquiries.view_any",
    VIEW_OWN: "contact_inquiries.view_own",
  },

  ORDERS: {
    VIEW_ANY: "orders.view_any",
    VIEW_OWN: "orders.view_own",
    STATUS_UPDATE_ANY: "orders.status_update_any",
    STATUS_UPDATE_OWN: "orders.status_update_own",
    PAYMENT_REVIEW_ANY: "orders.payment_review_any",
    PAYMENT_REVIEW_OWN: "orders.payment_review_own",
    DELIVERY_FEE_MANAGE_ANY: "orders.delivery_fee_manage_any",
    DELIVERY_FEE_MANAGE_OWN: "orders.delivery_fee_manage_own",
  },

  PROMOTIONS: {
    CREATE: "promotions.create",
    VIEW_ANY: "promotions.view_any",
    VIEW_OWN: "promotions.view_own",
    EDIT_ANY: "promotions.edit_any",
    EDIT_OWN: "promotions.edit_own",
    DELETE_ANY: "promotions.delete_any",
    DELETE_OWN: "promotions.delete_own",
  },

  CUSTOMER_RISK: {
    MANAGE_ANY: "customer_risk.manage_any",
    MANAGE_OWN: "customer_risk.manage_own",
  },

  INVENTORY: {
    VIEW_ANY: "inventory.view_any",
    VIEW_OWN: "inventory.view_own",
    ADJUST_ANY: "inventory.adjust_any",
    ADJUST_OWN: "inventory.adjust_own",
  },

  REVIEWS: {
    MODERATE_ANY: "reviews.moderate_any",
    MODERATE_OWN: "reviews.moderate_own",
  },

  REPORTS: {
    VIEW_ANY: "reports.view_any",
    VIEW_OWN: "reports.view_own",
  },

  COMMERCE_SETTINGS: {
    MANAGE_ANY: "commerce_settings.manage_any",
    MANAGE_OWN: "commerce_settings.manage_own",
  },

  WEBSITE_USERS: {
    VIEW_ANY: "website_users.view_any",
    VIEW_OWN: "website_users.view_own",
  },

  USERS_ROLES: {
    CREATE: "usersroles.create",
    VIEW_ANY: "usersroles.view_any",
    VIEW_OWN: "usersroles.view_own",
    EDIT_ANY: "usersroles.edit_any",
    EDIT_OWN: "usersroles.edit_own",
    DELETE_ANY: "usersroles.delete_any",
    DELETE_OWN: "usersroles.delete_own",
  },
} as const;

/**
 * Recursively extract all string leaf values from a nested object.
 */
type LeafValues<T> = T extends string
  ? T
  : T extends object
    ? { [K in keyof T]: LeafValues<T[K]> }[keyof T]
    : never;

export type Permission = LeafValues<typeof PERMISSIONS>;
